import React from 'react'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
import { graphql } from 'gatsby'
import MainLayout from '@toasttab/shared/src/layout/MainLayout'
import {
  getAllEntries,
  getLanguageByCountryCode,
  getLocalizedEntries,
  getSiteIdByLanguageCode
} from '@toasttab/shared/src/utils/siteUtils'
import SEO from '../components/Common/SEO/SEO'
import {
  PageContext,
  PageContextProps
} from '@toasttab/shared/src/hooks/usePageContext'
import { ContentfulImageAPI } from '@toasttab/shared/src/utils/contentfulUtils'
import Eyebrow from '@toasttab/shared/src/components/molecules/Eyebrow/Eyebrow'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import ImageResolutions from '@toasttab/shared/src/components/atoms/Image/ImageResolutions'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import { ResourceBodyOptions } from '@toasttab/shared/src/utils/Contentful/richText'
import MainFormContentful from '@toasttab/shared/src/components/molecules/Contentful/Form/MainFormContentful'

export const query = graphql`
  query ($locale: String!, $region: String!, $slug: String!) {
    allContentfulMdsResourcePage(
      filter: {
        node_locale: { eq: $locale }
        regions: { elemMatch: { country: { eq: $region } } }
        slug: { eq: $slug }
      }
    ) {
      edges {
        node {
          ...ResourcePage
        }
      }
    }
    allContentfulGlobalPageFooter {
      edges {
        node {
          internalName
          pageFooter {
            country
            ...FooterContent
          }
        }
      }
    }
    site {
      siteMetadata {
        twitterUsername
        title
        siteUrl
        hostName
        pathPrefix
        image
        description
      }
    }
  }
`

const ResourceDownloadPage = (props: any) => {
  const { language, region } = useI18next()
  const { data } = props
  const node = data.allContentfulMdsResourcePage.edges[0].node
  const title = node.title
  const siteId = getSiteIdByLanguageCode(getLanguageByCountryCode(language))
  const regionSite = node.regions.filter((r: any) => r.country == region)[0]
  const {
    headline,
    description,
    image,
    cardImage,
    contentBody,
    gatedPageForm
  } = regionSite
  const context: PageContextProps = {
    title: title,
    url: props.pageContext.url,
    region: regionSite.country,
    isLP: false
  }
  const allEntries = getAllEntries(node.slug, node.regions)
  const { t } = useI18next()
  // Set up page footer
  let footerContent
  // Use an override footer if it exists in the Contentful data
  if (node.footerOverride) {
    footerContent = node.footerOverride?.pageFooter?.filter(
      (f: any) => f.country === region
    )[0].footerContent
  } else {
    // Otherwise, use the standard site footer
    const mainFooter = data.allContentfulGlobalPageFooter.edges.filter(
      (f: any) => f.node.internalName === 'Main Site Footer'
    )[0].node
    footerContent = mainFooter.pageFooter.filter(
      (f: any) => f.country === region
    )[0].footerContent
  }

  const renderImage = () => {
    if (image) {
      let query = ContentfulImageAPI.transformImageAvif({
        width: 900,
        extension: image.file?.contentType
      })
      let srcsetWeb = ContentfulImageAPI.transformImageAvifSrcset({
        url: image.file?.url,
        widths: [458, 353],
        extension: image.file?.contentType
      })
      const imgResolutions = {
        imageResolutions: [
          {
            borderRadius: 'Small',
            desktop: [
              {
                optimizedImages: {
                  src: image.file?.url,
                  srcsetAvif: srcsetWeb ? srcsetWeb : image.file?.url,
                  srcAvif: image.file?.url + query
                },
                url: image.file?.url,
                title: image.title,
                extension: image.file?.contentType
              }
            ]
          }
        ]
      }
      return (
        <div className='w-full'>
          <ImageResolutions images={imgResolutions} classes='mt-6' />
        </div>
      )
    }
  }
  const renderThumbnailImage = () => {
    const img = cardImage || image
    if (img) {
      let query = ContentfulImageAPI.transformImageAvif({
        width: 88,
        extension: img.file?.contentType
      })
      let srcsetWeb = ContentfulImageAPI.transformImageAvifSrcset({
        url: img.file?.url,
        widths: [88],
        extension: img.file?.contentType
      })
      const imgResolutions = {
        imageResolutions: [
          {
            borderRadius: 'Small',
            desktop: [
              {
                optimizedImages: {
                  src: img.file?.url,
                  srcsetAvif: srcsetWeb ? srcsetWeb : img.file?.url,
                  srcAvif: img.file?.url + query
                },
                url: img.file?.url,
                title: img.title,
                extension: img.file?.contentType
              }
            ]
          }
        ]
      }
      return (
        <div className='max-w-[88px] mx-auto lg:hidden pb-[10px]'>
          <ImageResolutions images={imgResolutions} />
        </div>
      )
    }
  }

  return (
    <PageContext.Provider value={context}>
      <MainLayout
        siteId={siteId}
        siteMetadata={data.site.siteMetadata}
        footerContent={footerContent}
        packageName='main'
        localizedEntries={allEntries}
        currentLanguage={language}
      >
        <section className='py-0 lg:py-14'>
          <div className='ds-container !px-0'>
            <div className='mx-auto'>
              <div className='grid grid-cols-1 !gap-y-0 grid-cols-4 lg:grid-cols-6 lg:grid-cols-12 gap-x-0'>
                <div className='px-4 lg:px-0 mb-8 w-auto lg:mb-16 col-span-4 lg:col-span-5 order-2 lg:order-1'>
                  <Eyebrow
                    content={t('resource')}
                    variation='otl-orange'
                    componentClass='mb-3 lg:mb-4 mt-2 lg:mt-0 font-semibold leading-[18px] font-sansPro'
                  />
                  {headline && (
                    <Typography as='h2' variation='h2' classes='mb-6'>
                      {headline}
                    </Typography>
                  )}
                  {description && (
                    <div
                      className='mb-4 text-secondary'
                      dangerouslySetInnerHTML={{
                        __html: description.childMarkdownRemark.html
                      }}
                    />
                  )}
                  {renderImage()}
                  {contentBody && (
                    <div className='mt-3 sm:mt-10'>
                      {renderRichText(contentBody, ResourceBodyOptions)}
                    </div>
                  )}
                </div>
                <div className='mb-8 lg:mb-16 col-span-4 lg:col-span-6 lg:col-start-7 order-1 lg:order-2'>
                  <div className='bg-brand-50 lg:sticky lg:max-w-[588px] p-4 rounded-b-[24px] lg:rounded-[24px] sticky -top-10 lg:top-[80px]'>
                    <div className='bg-white p-6 rounded-[20px] [&_h3]:!text-left'>
                      {renderThumbnailImage()}
                      <MainFormContentful
                        {...gatedPageForm}
                        layout='fullWidth'
                        fieldIdPrefix='ResourceForm'
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </MainLayout>
    </PageContext.Provider>
  )
}
export default ResourceDownloadPage

export const Head = (props: any) => {
  const { data, pageContext } = props
  const node = data.allContentfulMdsResourcePage.edges[0].node
  const regionSite = node.regions.filter(
    (r: any) => r.country == pageContext.region
  )[0]
  const slug = regionSite.slugOverride ? regionSite.slugOverride : node.slug
  const currentRegion = regionSite.country
  const localizedEntries = getLocalizedEntries(
    slug,
    node.regions,
    currentRegion
  )
  return (
    <SEO
      title={regionSite.metaTitle}
      language={getLanguageByCountryCode(currentRegion)}
      localizedEntries={localizedEntries}
      description={regionSite.metaDescription}
      pathname={slug}
      noIndex={!regionSite.isIndexed}
    />
  )
}
